import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

/**
 * 対象のエレメントが省略表示されているかを判定する
 * * 監視対象のエレメントに ref="truncTarget" を付与すること
 * * 監視対象のエレメントはコンポーネント内で1つだけ有効(複数エレメントに ref="truncTarget" 指定は不可)
 * * setup 内で使用する場合は truncTarget を return すること
 *   ```
 *   const { truncTarget, isTruncated } = useIsTruncated()
 *   return { truncTarget, isTruncated }
 *   ```
 *   @see https://ja.vuejs.org/guide/essentials/template-refs.html#accessing-the-refs
 */
export function useIsTruncated() {
  /**
   * 監視対象エレメント
   * @type {Ref.<HTMLElement>}
   */
  const truncTarget = ref(null)
  /**
   * 省略表示状態
   * @type {Ref.<Boolean>}
   */
  const isTruncated = ref(false)
  /**
   * ResizeObserver(リアクティブでない)
   * @type {ResizeObserver}
   */
  let observer = null

  /**
   * 省略状態更新
   */
  const updateIsTruncated = () => {
    isTruncated.value =
      !!truncTarget.value &&
      truncTarget.value.scrollWidth > truncTarget.value.offsetWidth
  }

  onMounted(() => {
    observer = new ResizeObserver(() => updateIsTruncated())
    truncTarget.value && observer.observe(truncTarget.value)
  })

  onBeforeUnmount(() => {
    observer.disconnect()
  })

  watch(truncTarget, (v) => {
    if (v) {
      observer?.disconnect()
      observer.observe(v)
    }
  })

  return { truncTarget, isTruncated }
}
